import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Spin } from "antd";
import { PUBLIC_PREFIX_PATH } from "configs/AppConfig";

// URLs das lojas de aplicativos
const APP_STORE_URL = "https://apps.apple.com/app/id6744626140";
const PLAY_STORE_URL =
	"https://play.google.com/store/apps/details?id=io.treino";

/**
 * Página de redirecionamento para deep links do app mobile
 *
 * Esta página:
 * 1. Recebe parâmetros via query string (route, userId, tipo, etc)
 * 2. Tenta abrir o app mobile via deep link
 * 3. Se o app não abrir, mostra opção para baixar o app na loja
 * 4. Usa técnicas avançadas para detectar se o app está instalado
 */
const DeeplinkRedirect = () => {
	const location = useLocation();
	const [showFallback, setShowFallback] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isIOS, setIsIOS] = useState(false);
	const [isAndroid, setIsAndroid] = useState(false);

	useEffect(() => {
		// Obter parâmetros da query string
		const params = new URLSearchParams(location.search);
		const route = params.get("route") || "/app/dados-conta";
		const userId = params.get("userId");
		const tipo = params.get("tipo");
		const id = params.get("id");

		// Construir query string para passar parâmetros
		const queryParams = [];
		if (userId) queryParams.push(`userId=${encodeURIComponent(userId)}`);
		if (tipo) queryParams.push(`tipo=${encodeURIComponent(tipo)}`);
		if (id) queryParams.push(`id=${encodeURIComponent(id)}`);

		// Preservar outros parâmetros da query string
		params.forEach((value, key) => {
			if (!["route", "userId", "tipo", "id"].includes(key)) {
				queryParams.push(
					`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
				);
			}
		});

		const queryString =
			queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

		// Deep link do app mobile
		const mobileRoute = route.startsWith("/") ? route.substring(1) : route;
		const mobileDeepLink = `treinoio://app/${mobileRoute}${queryString}`;

		// Detectar dispositivo
		const detectedIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		const detectedAndroid = /android/i.test(navigator.userAgent);
		const isMobile = detectedIOS || detectedAndroid;

		// Atualizar estado para usar no fallback
		setIsIOS(detectedIOS);
		setIsAndroid(detectedAndroid);

		let appOpened = false;
		let pageHidden = false;
		const startTime = Date.now();
		const timeout = 2000; // 2 segundos para detectar se app abriu
		const maxWaitTime = 4000; // 4 segundos máximo antes de mostrar fallback

		// Técnica melhorada para Android: usar intent:// URL
		const getAndroidIntentUrl = () => {
			// Intent URL é mais confiável no Android
			const intentUrl = `intent://app/${mobileRoute}${queryString}#Intent;scheme=treinoio;package=io.treino;end`;
			return intentUrl;
		};

		// iOS: usar location.href diretamente (mais confiável)

		// Listener para detectar se a página foi escondida (app foi aberto)
		const handleVisibilityChange = () => {
			if (document.hidden) {
				pageHidden = true;
				// Página foi escondida, provavelmente o app abriu
				appOpened = true;
			}
		};

		// Listener para detectar se a página perdeu foco (app foi aberto)
		const handleBlur = () => {
			const timeElapsed = Date.now() - startTime;
			if (timeElapsed < 1500) {
				// Se perdeu foco rapidamente, provavelmente o app abriu
				pageHidden = true;
				appOpened = true;
			}
		};

		// Listener para detectar se voltou o foco
		const handleFocus = () => {
			const timeElapsed = Date.now() - startTime;
			// Se voltou o foco após um tempo, provavelmente o app não abriu
			if (timeElapsed > timeout && !appOpened) {
				setShowFallback(true);
				setLoading(false);
			}
		};

		// Função para tentar abrir o app
		const tryOpenApp = () => {
			if (appOpened) return;

			try {
				// Adicionar listeners antes de tentar abrir
				document.addEventListener(
					"visibilitychange",
					handleVisibilityChange,
				);
				window.addEventListener("blur", handleBlur);
				window.addEventListener("focus", handleFocus);

				if (detectedAndroid) {
					// Android: usar intent URL que é mais confiável
					const intentUrl = getAndroidIntentUrl();
					window.location.href = intentUrl;
				} else if (detectedIOS) {
					// iOS: usar location.href diretamente (método mais confiável)
					window.location.href = mobileDeepLink;
				} else {
					// Desktop ou outro: tentar mesmo assim
					window.location.href = mobileDeepLink;
				}

				appOpened = true;

				// Verificar se o app foi aberto após timeout
				setTimeout(() => {
					const timeElapsed = Date.now() - startTime;
					if (
						!pageHidden &&
						timeElapsed > timeout &&
						document.hasFocus()
					) {
						// Se ainda estamos na página e não escondeu, provavelmente o app não abriu
						setShowFallback(true);
						setLoading(false);
					}
				}, timeout);
			} catch (error) {
				console.error("Erro ao tentar abrir app:", error);
				setShowFallback(true);
				setLoading(false);
			}
		};

		// Tentar abrir o app imediatamente
		tryOpenApp();

		// Fallback automático após maxWaitTime
		const fallbackTimeout = setTimeout(() => {
			if (!pageHidden && document.hasFocus()) {
				// Se ainda estamos na página, mostrar fallback
				setShowFallback(true);
				setLoading(false);
			}
		}, maxWaitTime);

		// Cleanup
		return () => {
			clearTimeout(fallbackTimeout);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
			window.removeEventListener("blur", handleBlur);
			window.removeEventListener("focus", handleFocus);
		};
	}, [location]);

	const handleDownloadApp = () => {
		if (isIOS) {
			window.open(APP_STORE_URL, "_blank");
		} else if (isAndroid) {
			window.open(PLAY_STORE_URL, "_blank");
		} else {
			// Desktop: mostrar ambas as opções
			window.open(APP_STORE_URL, "_blank");
			setTimeout(() => {
				window.open(PLAY_STORE_URL, "_blank");
			}, 500);
		}
	};

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				height: "100vh",
				background: "linear-gradient(135deg, #ff8034 0%, #ff6b1a 100%)",
				flexDirection: "column",
				color: "white",
				fontFamily:
					"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
				padding: "2rem",
			}}
		>
			{loading && !showFallback && (
				<>
					<img
						src={`${PUBLIC_PREFIX_PATH}/logo-comprido.png`}
						alt="Treino.io"
						style={{
							maxWidth: "280px",
							width: "100%",
							height: "auto",
							marginBottom: "2rem",
							filter: "brightness(0) invert(1)",
						}}
					/>
					<Spin size="large" style={{ color: "white" }} />
					<div
						style={{
							marginTop: "1.5rem",
							fontSize: "1.2rem",
							fontWeight: 500,
						}}
					>
						Abrindo o app...
					</div>
				</>
			)}

			{showFallback && (
				<div
					style={{
						textAlign: "center",
						padding: "2rem",
						maxWidth: "500px",
						width: "100%",
					}}
				>
					<img
						src={`${PUBLIC_PREFIX_PATH}/logo-comprido.png`}
						alt="Treino.io"
						style={{
							maxWidth: "280px",
							width: "100%",
							height: "auto",
							marginBottom: "2rem",
							filter: "brightness(0) invert(1)",
						}}
					/>
					<div
						style={{
							fontSize: "1.5rem",
							marginBottom: "0.5rem",
							fontWeight: 600,
						}}
					>
						App não encontrado
					</div>
					<div
						style={{
							fontSize: "1rem",
							marginBottom: "2rem",
							opacity: 0.9,
						}}
					>
						{isIOS || isAndroid
							? "Baixe o app Treino.io para abrir este conteúdo"
							: "Baixe o app Treino.io no seu dispositivo móvel"}
					</div>

					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "1rem",
							width: "100%",
						}}
					>
						<Button
							type="primary"
							size="large"
							onClick={handleDownloadApp}
							style={{
								background: "white",
								color: "#ff8034",
								border: "none",
								fontWeight: 600,
								height: "48px",
								fontSize: "1rem",
							}}
						>
							{isIOS
								? "Baixar na App Store"
								: isAndroid
								? "Baixar na Play Store"
								: "Baixar o App"}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

export default DeeplinkRedirect;
